import { jsPDF } from 'jspdf';
import { formatAmountInWords as formatAmountInWordsBySystem, formatCurrency as formatCurrencyBySystem } from './numberingSystem';

export const generateInvoicePDF = async (invoice, orgDetails, logoData, companyName, type, selectedBankAccount = null, pdfOptions = null) => {
  try {
    const resolvedLogoInlineData = logoData?.pdfDataUrl || null;
    const resolvedLogoUrl = logoData?.fullUrl || logoData?.logoPath || null;

    // Create new PDF document
    const doc = new jsPDF();
    
    // Helper functions
    // Use a fixed DD/MM/YYYY format instead of toLocaleDateString(), which
    // otherwise renders differently depending on each viewer's browser/OS locale.
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'N/A';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const formatCurrency = (amount) => {
      return formatCurrencyBySystem(
        amount,
        orgDetails?.metadata?.numberingSystem || 'indian',
        { prefix: orgDetails?.metadata?.currency?.prefix || 'Rs.' }
      );
    };

    const formatAmountInWords = (amount) => {
      return formatAmountInWordsBySystem(
        amount,
        orgDetails?.metadata?.numberingSystem || 'indian',
        { currencyCode: orgDetails?.metadata?.currency?.code || 'INR' }
      );
    };

    const getCustomerName = (customer) => {
      if (typeof customer === 'string') return customer;
      if (customer && customer.name) return customer.name;
      return 'Unknown Customer';
    };

    const renderCustomerDetailsPDF = (customer) => {
      if (typeof customer === 'string') {
        return { name: customer, address: '', contact: '', email: '', gst: '' };
      }
      
      if (customer && typeof customer === 'object') {
        return {
          name: customer.name || 'Unknown Customer',
          address: customer.address || '',
          contact: customer.contact || customer.mobile || customer.phone || '',
          email: customer.email || '',
          gst: customer.gst || ''
        };
      }
      
      return { name: 'Unknown Customer', address: '', contact: '', email: '', gst: '' };
    };

    const normalizePdfText = (value, fallback = 'N/A') => {
      if (value === undefined || value === null) return fallback;

      const normalized = String(value)
        .replace(/\r\n?/g, '\n')
        .replace(/[\u2028\u2029]/g, '\n')
        .replace(/\u00A0/g, ' ')
        .replace(/[\u2022\u2023\u25E6\u2043\u2219\uF0B7]/g, '-')
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/[‐‑‒–—]/g, '-')
        .replace(/\t/g, ' ')
        .replace(/[^\S\n]+/g, ' ')
        .split('\n')
        .map((line) => line.trim())
        .join('\n')
        .trim();

      return normalized || fallback;
    };

    const forceWrapLine = (line, maxWidth) => {
      const safeLine = String(line || '');
      if (!safeLine) return [''];
      if (doc.getTextWidth(safeLine) <= maxWidth) return [safeLine];

      const chunks = [];
      let currentChunk = '';

      for (const char of safeLine) {
        const nextChunk = currentChunk + char;
        if (currentChunk && doc.getTextWidth(nextChunk) > maxWidth) {
          chunks.push(currentChunk);
          currentChunk = char;
        } else {
          currentChunk = nextChunk;
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk);
      }

      return chunks;
    };

    const wrapPdfText = (value, maxWidth, fallback = 'N/A') => {
      const normalized = normalizePdfText(value, fallback);
      return normalized.split('\n').flatMap((paragraph) => {
        if (!paragraph) return [''];
        const wrapped = doc.splitTextToSize(paragraph, maxWidth);
        return wrapped.flatMap((line) => forceWrapLine(line, maxWidth));
      });
    };

    const footerCompanyName = String(companyName || orgDetails?.name || '').trim();
    const footerAddress = normalizePdfText(orgDetails?.address || '', '');
    const footerContactNumbers = Array.isArray(orgDetails?.contactNumbers)
      ? orgDetails.contactNumbers.filter(Boolean)
      : (orgDetails?.phone ? [orgDetails.phone] : []);
    const footerWebsites = Array.isArray(orgDetails?.websites)
      ? orgDetails.websites.filter(Boolean)
      : [];
    const footerEmail = String(orgDetails?.contactEmail || orgDetails?.email || '').trim();
    const footerContactLineParts = [
      footerContactNumbers.length > 0 ? `Phone: ${footerContactNumbers.join(', ')}` : '',
      footerEmail ? `Email: ${footerEmail}` : ''
    ].filter(Boolean);
    const footerLineSources = [
      footerCompanyName,
      footerAddress ? footerAddress.replace(/\s*\n\s*/g, ' ') : '',
      footerContactLineParts.join(' | '),
      footerWebsites.length > 0 ? `Website: ${footerWebsites.join(', ')}` : ''
    ].filter((value) => String(value || '').trim());
    const footerInfoLines = footerLineSources.map((line) => String(line).trim()).filter(Boolean);

    const loadImageAsBase64 = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        if (!/^data:/i.test(String(url || ''))) {
          img.crossOrigin = 'anonymous';
        }
        
        img.onload = function() {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Fill with white background to handle transparency
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw the image on top of white background
          ctx.drawImage(img, 0, 0);
          
          try {
            // Use JPEG format with white background
            const dataURL = canvas.toDataURL('image/jpeg', 0.9);
            resolve(dataURL);
          } catch (error) {
            reject(error);
          }
        };
        
        img.onerror = function() {
          reject(new Error('Failed to load image'));
        };
        
        img.src = url;
      });
    };

    const loadLogoAsset = async () => {
      const sources = [resolvedLogoInlineData, resolvedLogoUrl].filter(Boolean);
      if (sources.length === 0) return null;

      for (const source of sources) {
        try {
          const dataUrl = await loadImageAsBase64(source);
          const dimensions = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.width || 1, height: img.height || 1 });
            img.onerror = () => resolve({ width: 1, height: 1 });
            img.src = dataUrl;
          });

          return {
            dataUrl,
            width: dimensions.width,
            height: dimensions.height
          };
        } catch (error) {
          console.warn('Could not embed logo source in PDF:', error);
        }
      }

      return null;
    };

    const logoAsset = await loadLogoAsset();

    const calculateSubtotal = () => {
      if (!invoice.products || !Array.isArray(invoice.products)) return 0;
      return invoice.products.reduce((sum, product) => {
        const rate = Number(product.price || product.rate) || 0;
        const quantity = Number(product.quantity) || 1;
        return sum + (rate * quantity);
      }, 0);
    };

    const calculateTotalTax = () => {
      if (!invoice.products || !Array.isArray(invoice.products)) return 0;
      return invoice.products.reduce((sum, product) => {
        const rate = Number(product.rate || product.price) || 0;
        const tax = Number(product.tax) || 0;
        const quantity = Number(product.quantity) || 1;
        return sum + (rate * tax / 100 * quantity);
      }, 0);
    };

    const getTaxCategory = (taxType) => {
      const normalized = String(taxType || '').toUpperCase();
      if (normalized.includes('IGST')) {
        return 'IGST';
      }
      return 'CGST/SGST';
    };

    const calculateTaxBreakdown = () => {
      if (!invoice.products || !Array.isArray(invoice.products)) {
        return { IGST: 0, 'CGST/SGST': 0 };
      }
      return invoice.products.reduce(
        (totals, product) => {
          const rate = Number(product.rate || product.price) || 0;
          const tax = Number(product.tax) || 0;
          const quantity = Number(product.quantity) || 1;
          const taxAmount = (rate * tax / 100 * quantity);
          const category = getTaxCategory(product.taxType);
          totals[category] = (totals[category] || 0) + taxAmount;
          return totals;
        },
        { IGST: 0, 'CGST/SGST': 0 }
      );
    };

    let currentY = 12;
    const continuationContentTopY = 32;
    const additionalFooterTopY = 268;
    const additionalFooterBottomY = 282;
    const additionalFooterLineHeight = 3.1;
    const continuationContentBottomY = additionalFooterTopY - 2;
    const pagesWithAdditionalChrome = new Set();

    const renderLogoInBox = (asset, x, y, maxWidth, maxHeight) => {
      if (!asset?.dataUrl) return false;

      const aspectRatio = (asset.width || 1) / (asset.height || 1);
      let logoWidth = maxWidth;
      let logoHeight = maxWidth / aspectRatio;

      if (logoHeight > maxHeight) {
        logoHeight = maxHeight;
        logoWidth = maxHeight * aspectRatio;
      }

      const logoX = x + (maxWidth - logoWidth);
      const logoY = y + ((maxHeight - logoHeight) / 2);
      doc.addImage(asset.dataUrl, 'JPEG', logoX, logoY, logoWidth, logoHeight);
      return true;
    };

    const drawPageBorder = () => {
      doc.setLineWidth(0.3);
      doc.rect(15, 10, 180, 277); // Full page border with margins
    };

    const drawAdditionalPageChrome = () => {
      const currentPageNumber = doc.getCurrentPageInfo().pageNumber;
      if (currentPageNumber <= 1 || pagesWithAdditionalChrome.has(currentPageNumber)) {
        return;
      }

      if (logoAsset?.dataUrl) {
        renderLogoInBox(logoAsset, 142, 11, 46, 17);
      }

      doc.setLineWidth(0.2);
      doc.line(15, additionalFooterTopY, 195, additionalFooterTopY);

      if (footerInfoLines.length > 0) {
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        const footerTextTopY = additionalFooterTopY + 3.5;
        const availableFooterHeight = Math.max(0, additionalFooterBottomY - footerTextTopY);
        const totalTextHeight = (footerInfoLines.length - 1) * additionalFooterLineHeight;
        const footerBaseY = footerTextTopY + Math.max(0, (availableFooterHeight - totalTextHeight) / 2);
        footerInfoLines.forEach((line, index) => {
          doc.text(line, 105, footerBaseY + (index * additionalFooterLineHeight), { align: 'center', maxWidth: 140 });
        });
      }

      pagesWithAdditionalChrome.add(currentPageNumber);
    };

    const addNewPage = () => {
      doc.addPage();
      drawPageBorder();
      drawAdditionalPageChrome();
      currentY = continuationContentTopY;
    };

    const getCurrentContentBottomY = () => (
      doc.getCurrentPageInfo().pageNumber > 1 ? continuationContentBottomY : 277
    );

    const resolveFontStyle = (style) => {
      if (style.bold && style.italic) return 'bolditalic';
      if (style.bold) return 'bold';
      if (style.italic) return 'italic';
      return 'normal';
    };

    const parseTermsMarkup = (rawInput) => {
      const text = normalizePdfText(rawInput, '');
      if (!text) return [];

      const tokens = text.split(/(<\/?[^>]+>)/g).filter((token) => token !== '');
      const lines = [[]];
      let boldDepth = 0;
      let italicDepth = 0;
      let underlineDepth = 0;

      const currentStyle = () => ({
        bold: boldDepth > 0,
        italic: italicDepth > 0,
        underline: underlineDepth > 0
      });

      const ensureLine = () => {
        if (lines.length === 0) lines.push([]);
      };

      const pushNewLine = () => {
        lines.push([]);
      };

      const pushSegment = (segmentText) => {
        if (!segmentText) return;
        ensureLine();

        const line = lines[lines.length - 1];
        const style = currentStyle();
        const previous = line[line.length - 1];

        if (
          previous &&
          previous.bold === style.bold &&
          previous.italic === style.italic &&
          previous.underline === style.underline
        ) {
          previous.text += segmentText;
        } else {
          line.push({ text: segmentText, ...style });
        }
      };

      const pushText = (content) => {
        const parts = String(content || '').split('\n');
        parts.forEach((part, index) => {
          if (part) pushSegment(part);
          if (index < parts.length - 1) pushNewLine();
        });
      };

      tokens.forEach((token) => {
        const trimmed = token.trim();
        const isTag = /^<\/?[^>]+>$/.test(trimmed);
        if (!isTag) {
          pushText(token);
          return;
        }

        const inner = trimmed.slice(1, -1).trim().toLowerCase();
        if (!inner || inner.startsWith('!')) return;

        const isClosing = inner.startsWith('/');
        const tagBody = isClosing ? inner.slice(1).trim() : inner;
        const tagName = tagBody.replace(/\/$/, '').split(/\s+/)[0];
        const isSelfClosing = tagBody.endsWith('/') || tagName === 'br';

        if (tagName === 'br') {
          pushNewLine();
          return;
        }

        if (tagName === 'p') {
          pushNewLine();
          if (isClosing) pushNewLine();
          return;
        }

        const openTag = () => {
          if (tagName === 'b' || tagName === 'strong') boldDepth += 1;
          if (tagName === 'i' || tagName === 'em') italicDepth += 1;
          if (tagName === 'u') underlineDepth += 1;
        };

        const closeTag = () => {
          if (tagName === 'b' || tagName === 'strong') boldDepth = Math.max(0, boldDepth - 1);
          if (tagName === 'i' || tagName === 'em') italicDepth = Math.max(0, italicDepth - 1);
          if (tagName === 'u') underlineDepth = Math.max(0, underlineDepth - 1);
        };

        if (isClosing) {
          closeTag();
          return;
        }

        openTag();
        if (isSelfClosing) {
          closeTag();
        }
      });

      return lines;
    };

    const wrapStyledSegments = (segments, maxWidth) => {
      const wrappedLines = [];
      let currentLine = [];
      let currentWidth = 0;

      const pushLine = () => {
        wrappedLines.push(currentLine);
        currentLine = [];
        currentWidth = 0;
      };

      const pushTokenToLine = (token, style) => {
        if (!token) return;
        doc.setFont('helvetica', resolveFontStyle(style));
        const tokenWidth = doc.getTextWidth(token);

        if (currentLine.length > 0 && currentWidth + tokenWidth > maxWidth) {
          pushLine();
        }

        const normalizedToken = currentLine.length === 0 ? token.replace(/^\s+/, '') : token;
        if (!normalizedToken) return;

        doc.setFont('helvetica', resolveFontStyle(style));
        const normalizedWidth = doc.getTextWidth(normalizedToken);

        if (normalizedWidth > maxWidth) {
          const chunks = forceWrapLine(normalizedToken, maxWidth);
          chunks.forEach((chunk, index) => {
            if (index > 0) pushLine();
            currentLine.push({ text: chunk, ...style });
            doc.setFont('helvetica', resolveFontStyle(style));
            currentWidth += doc.getTextWidth(chunk);
          });
          return;
        }

        currentLine.push({ text: normalizedToken, ...style });
        currentWidth += normalizedWidth;
      };

      segments.forEach((segment) => {
        const style = {
          bold: !!segment.bold,
          italic: !!segment.italic,
          underline: !!segment.underline
        };
        const parts = String(segment.text || '').split(/(\s+)/);
        parts.forEach((part) => pushTokenToLine(part, style));
      });

      if (currentLine.length > 0 || wrappedLines.length === 0) {
        wrappedLines.push(currentLine);
      }

      return wrappedLines;
    };

    const drawStyledTermsLine = (segments, startX, baselineY) => {
      let cursorX = startX;
      segments.forEach((segment) => {
        const style = {
          bold: !!segment.bold,
          italic: !!segment.italic,
          underline: !!segment.underline
        };
        doc.setFont('helvetica', resolveFontStyle(style));
        doc.text(segment.text, cursorX, baselineY, { maxWidth: 190 - startX });
        const segmentWidth = doc.getTextWidth(segment.text);

        if (style.underline && segment.text.trim()) {
          const underlineY = baselineY + 0.8;
          doc.setLineWidth(0.2);
          doc.line(cursorX, underlineY, cursorX + segmentWidth, underlineY);
        }

        cursorX += segmentWidth;
      });
    };

    const renderTermsAndConditionsAppendix = (rawTermsText) => {
      const parsedLines = parseTermsMarkup(rawTermsText);
      if (parsedLines.length === 0) return;

      const termsLeftX = 18;
      const termsRightX = 192;
      const termsContentWidth = termsRightX - termsLeftX;
      const termsLineHeight = 4.5;

      const ensureTermsPageSpace = () => {
        if (currentY + termsLineHeight > continuationContentBottomY) {
          startTermsPage(true);
        }
      };

      const startTermsPage = (isContinuation = false) => {
        doc.addPage();
        drawPageBorder();
        drawAdditionalPageChrome();
        currentY = continuationContentTopY;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(isContinuation ? 'TERMS AND CONDITIONS (contd.)' : 'TERMS AND CONDITIONS', termsLeftX, currentY);
        currentY += 8;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
      };

      startTermsPage(false);

      parsedLines.forEach((lineSegments) => {
        if (!lineSegments || lineSegments.length === 0) {
          ensureTermsPageSpace();
          currentY += termsLineHeight;
          return;
        }

        const wrappedStyledLines = wrapStyledSegments(lineSegments, termsContentWidth);

        wrappedStyledLines.forEach((wrappedLine) => {
          ensureTermsPageSpace();
          drawStyledTermsLine(wrappedLine, termsLeftX, currentY);
          currentY += termsLineHeight;
        });
      });
    };

    // Add border around entire PDF
    drawPageBorder();

    // 1. Add "CUSTOMER COPY" in small text at top left with even padding
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const customerCopyText = 'CUSTOMER COPY';
    const customerCopyPadding = 2;
    const customerCopyBoxHeight = 6;
    const customerCopyTextWidth = doc.getTextWidth(customerCopyText);
    const customerCopyBoxWidth = customerCopyTextWidth + (customerCopyPadding * 2);
    const customerCopyBoxX = 17;
    const customerCopyBoxY = currentY;
    doc.setLineWidth(0.2);
    doc.rect(customerCopyBoxX, customerCopyBoxY, customerCopyBoxWidth, customerCopyBoxHeight);
    doc.text(customerCopyText, customerCopyBoxX + customerCopyPadding, customerCopyBoxY + 4.2);
    currentY = customerCopyBoxY + customerCopyBoxHeight + 4;

    const leftColumnX = 15; // Align with outer border
    const rightColumnX = 105;
    const headerWidth = 180; // Match outer border width
    const columnWidth = 90;

    const orgAddressLines = orgDetails?.address
      ? wrapPdfText(orgDetails.address, columnWidth - 10, '')
      : [];
    const contactNumbers = Array.isArray(orgDetails?.contactNumbers)
      ? orgDetails.contactNumbers.filter(Boolean)
      : (orgDetails?.phone ? [orgDetails.phone] : []);
    const contactLine = contactNumbers.length > 0 ? `Phone: ${contactNumbers.join(', ')}` : '';
    const contactLines = contactLine
      ? wrapPdfText(contactLine, columnWidth - 10, '')
      : [];
    const websites = Array.isArray(orgDetails?.websites) ? orgDetails.websites.filter(Boolean) : [];
    const websiteLine = websites.length > 0 ? `Website: ${websites.join(', ')}` : '';
    const websiteLines = websiteLine
      ? wrapPdfText(websiteLine, columnWidth - 10, '')
      : [];
    const headerContentHeight =
      8 + // top padding
      6 + // org name
      (orgAddressLines.length * 5) +
      (contactLines.length * 5) +
      (websiteLines.length * 5) +
      (orgDetails?.gst ? 5 : 0) +
      4; // bottom padding

    // 2. Create two-column header with borders
    const headerHeight = Math.max(35, headerContentHeight); // Ensure enough space for content
    const headerStartY = currentY;

    // Draw header borders
    doc.setLineWidth(0.2); // Reduced from 0.5 to 0.2
    // Outer border
    doc.rect(leftColumnX, headerStartY, headerWidth, headerHeight);
    // Middle divider
    doc.line(rightColumnX, headerStartY, rightColumnX, headerStartY + headerHeight);

    // 3. Add organization details to left column
    let leftColumnY = headerStartY + 8;
    if (orgDetails) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(orgDetails.name || 'Organization', leftColumnX + 5, leftColumnY);
      leftColumnY += 6;
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      if (orgAddressLines.length > 0) {
        for (let i = 0; i < orgAddressLines.length; i++) {
          doc.text(orgAddressLines[i], leftColumnX + 5, leftColumnY);
          leftColumnY += 5;
        }
      }
      
      if (contactLines.length > 0) {
        contactLines.forEach((line) => {
          doc.text(line, leftColumnX + 5, leftColumnY);
          leftColumnY += 5;
        });
      }

      if (websiteLines.length > 0) {
        websiteLines.forEach((line) => {
          doc.text(line, leftColumnX + 5, leftColumnY);
          leftColumnY += 5;
        });
      }
      
      if (orgDetails.gst) {
        doc.text(`GST: ${orgDetails.gst}`, leftColumnX + 5, leftColumnY);
      }
    }

    // 4. Add logo to right column - Add logo if available
    if (logoAsset?.dataUrl) {
      const logoBoxWidth = columnWidth * 0.63;
      const logoBoxHeight = headerHeight * 0.63;
      const logoBoxX = rightColumnX + ((columnWidth - logoBoxWidth) / 2);
      const logoBoxY = headerStartY + ((headerHeight - logoBoxHeight) / 2);
      renderLogoInBox(logoAsset, logoBoxX, logoBoxY, logoBoxWidth, logoBoxHeight);
    } else if (resolvedLogoUrl) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text('Logo unavailable', rightColumnX + 5, headerStartY + 20);
    }

    currentY = headerStartY + headerHeight + 8; // Increased spacing before INVOICE

    // 5. Add document type text (Quotation or Invoice)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text((type).toUpperCase(), 105, currentY, { align: 'center' });
    currentY += 8; // Increased spacing after document type

    // 6. Create customer/invoice info table with adaptive height
    const tableStartY = currentY;
    const customerDetails = renderCustomerDetailsPDF(invoice.customer);
    const customerAddressLines = customerDetails.address
      ? wrapPdfText(customerDetails.address, columnWidth - 10, '')
      : [];
    const leftColumnContentHeight =
      8 + // top padding
      6 + // "Bill To" line
      5 + // customer name
      (customerAddressLines.length * 4) +
      (customerDetails.contact ? 4 : 0) +
      8 + // email line
      8; // gst line
    const rightColumnContentHeight =
      8 + // top padding
      8 + // "Invoice Details" line
      6 + // invoice number
      6 + // date
      4; // bottom padding
    const tableHeight = Math.max(25, leftColumnContentHeight, rightColumnContentHeight);
    
    // Draw table borders
    doc.setLineWidth(0.2); // Set thin border width
    doc.rect(leftColumnX, tableStartY, headerWidth, tableHeight);
    doc.line(rightColumnX, tableStartY, rightColumnX, tableStartY + tableHeight);
    
    // 7. Add customer address to left column
    let customerY = tableStartY + 8;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', leftColumnX + 5, customerY);
    customerY += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(customerDetails.name, leftColumnX + 5, customerY);
    customerY += 5;
    
    if (customerAddressLines.length > 0) {
      for (let i = 0; i < customerAddressLines.length; i++) {
        doc.text(customerAddressLines[i], leftColumnX + 5, customerY);
        customerY += 4;
      }
    }
    
    if (customerDetails.contact) {
      doc.text(customerDetails.contact, leftColumnX + 5, customerY);
      customerY += 4;
    }

    const emailDisplay = customerDetails.email || '-';
    doc.text(`Email: ${emailDisplay}`, leftColumnX + 5, customerY);
    customerY += 4;

    const gstDisplay = customerDetails.gst || '-';
    doc.text(`GST: ${gstDisplay}`, leftColumnX + 5, customerY);
    customerY += 4;
    
    // 8. Add invoice number and date to right column
    let invoiceY = tableStartY + 8;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Details:', rightColumnX + 5, invoiceY);
    invoiceY += 8;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${type || 'Invoice'} #: ${invoice.documentNumber || 'N/A'}`, rightColumnX + 5, invoiceY);
    invoiceY += 6;
    doc.text(`Date: ${formatDate(invoice.createdAt)}`, rightColumnX + 5, invoiceY);
    
    currentY = tableStartY + tableHeight + 4; // Further reduced spacing

    // Reserve space for footer/payment table on last page
    const endPageY = 287; // Bottom of outer border (outer border ends at Y=287)
    const paymentTableHeight = 60;
    const getPaymentTableStartY = () => {
      const isAdditionalPage = doc.getCurrentPageInfo().pageNumber > 1;
      return isAdditionalPage
        ? additionalFooterTopY - paymentTableHeight
        : endPageY - paymentTableHeight;
    };

    // Products Table
    if (invoice.products && invoice.products.length > 0) {
      const tableLeftX = 15;
      const tableRightX = 195;
      const tableWidth = tableRightX - tableLeftX;
      const columnPadding = 2;
      const requiredColumns = ['sl', 'description', 'qty', 'total'];
      const defaultColumns = {
        sl: true,
        description: true,
        hsn: false,
        rate: true,
        qty: true,
        amountExclTax: false,
        tax: false,
        taxType: false,
        taxAmt: false,
        total: true
      };
      const pdfKey = (type || 'Invoice').toLowerCase();
      const activePdfOptions = pdfOptions || orgDetails?.metadata?.pdfOptions?.[pdfKey] || {};
      const selectedColumns = {
        ...defaultColumns,
        ...(activePdfOptions.columns || {})
      };
      requiredColumns.forEach((key) => {
        selectedColumns[key] = true;
      });
      const columnDefs = [
        { key: 'sl', label: '#', weight: 0.04 },
        { key: 'description', label: 'Description', weight: 0.2 },
        { key: 'hsn', label: 'HSN', weight: 0.11 },
        { key: 'rate', label: 'Rate', weight: 0.108 },
        { key: 'qty', label: 'Qty', weight: 0.084 },
        { key: 'amountExclTax', label: 'Amount (excl. Tax)', weight: 0.14 },
        { key: 'tax', label: 'Tax%', weight: 0.084 },
        { key: 'taxType', label: 'Type', weight: 0.084 },
        { key: 'taxAmt', label: 'Tax Amt', weight: 0.11 },
        { key: 'total', label: 'Amount (incl. Tax)', weight: 0.11 }
      ];
      const activeColumns = columnDefs.filter((column) => selectedColumns[column.key]);
      // All non-description columns get a fixed width derived from the full weight
      // set (regardless of which columns are toggled on/off); the description
      // column always absorbs whatever width remains, growing or shrinking as
      // other columns are shown/hidden.
      const fullWeightTotal = columnDefs.reduce((sum, column) => sum + column.weight, 0) || 1;
      const weightWidth = (column) => tableWidth * (column.weight / fullWeightTotal);

      // Measure the widest single-line content per non-description column (header
      // label plus every row's value) so that column can grow to fit without
      // wrapping — the saved space is always borrowed from Description only.
      doc.setFontSize(8);
      const measureCellWidth = (text) => doc.getTextWidth(String(text ?? '')) + (columnPadding * 2);
      const naturalColumnWidths = {};
      activeColumns.forEach((column) => {
        if (column.key === 'description') return;
        naturalColumnWidths[column.key] = measureCellWidth(column.label);
      });
      invoice.products.forEach((product, productIndex) => {
        const rate = Number(product.price || product.rate) || 0;
        const tax = Number(product.tax) || 0;
        const quantity = Number(product.quantity) || 1;
        const lineAmount = rate * quantity;
        const taxAmount = lineAmount * (tax / 100);
        const total = lineAmount + taxAmount;
        const hsnValue = product.hsncode ?? product.hsnCode ?? product.hsn ?? product.productId?.hsncode ?? product.productId?.hsnCode ?? '-';
        const candidateValues = {
          sl: (productIndex + 1).toString(),
          hsn: hsnValue.toString(),
          rate: formatCurrency(rate),
          qty: quantity.toString(),
          amountExclTax: formatCurrency(lineAmount),
          tax: `${tax}%`,
          taxType: product.taxType || '-',
          taxAmt: formatCurrency(taxAmount),
          total: formatCurrency(total)
        };
        Object.keys(candidateValues).forEach((key) => {
          if (!(key in naturalColumnWidths)) return;
          const width = measureCellWidth(candidateValues[key]);
          if (width > naturalColumnWidths[key]) {
            naturalColumnWidths[key] = width;
          }
        });
      });

      // Description must always keep at least its fair weighted share (or a
      // sensible absolute minimum) of the table so a crowded combination of
      // optional columns can never squeeze it — or, worse, the trailing
      // column — down to an unusable/negative width.
      const descriptionColumnDef = columnDefs.find((column) => column.key === 'description');
      const minDescriptionWidth = Math.max(weightWidth(descriptionColumnDef), 20);
      const naturalNonDescriptionWidths = {};
      activeColumns
        .filter((column) => column.key !== 'description')
        .forEach((column) => {
          naturalNonDescriptionWidths[column.key] = Math.max(weightWidth(column), naturalColumnWidths[column.key] || 0);
        });
      // Rate: widen by the space of 6 extra digits so multi-digit rates have
      // extra breathing room and never wrap.
      if (naturalNonDescriptionWidths.rate !== undefined) {
        naturalNonDescriptionWidths.rate += doc.getTextWidth('000000');
      }
      // Tax Type: its content is always short, so its width is capped at half
      // of what it would otherwise get.
      if (naturalNonDescriptionWidths.taxType !== undefined) {
        naturalNonDescriptionWidths.taxType *= 0.5;
      }
      // Qty: shrink by 30% and hand that reclaimed space to Tax Type, whose
      // short content can make good use of it.
      if (naturalNonDescriptionWidths.qty !== undefined) {
        const qtyReduction = naturalNonDescriptionWidths.qty * 0.3;
        naturalNonDescriptionWidths.qty -= qtyReduction;
        if (naturalNonDescriptionWidths.taxType !== undefined) {
          naturalNonDescriptionWidths.taxType += qtyReduction;
        }
      }
      const nonDescriptionWidth = Object.values(naturalNonDescriptionWidths).reduce((sum, width) => sum + width, 0);
      const availableForNonDescription = Math.max(0, tableWidth - minDescriptionWidth);
      // When every selected column's natural (no-wrap) width can't fit alongside
      // the description's minimum share, shrink all of them proportionally
      // instead of letting the overflow silently collapse onto whichever
      // column happens to be last.
      const nonDescriptionShrinkFactor = nonDescriptionWidth > availableForNonDescription && nonDescriptionWidth > 0
        ? availableForNonDescription / nonDescriptionWidth
        : 1;
      const finalNonDescriptionWidths = {};
      Object.keys(naturalNonDescriptionWidths).forEach((key) => {
        const minColumnWidth = Math.min(naturalNonDescriptionWidths[key], 8);
        finalNonDescriptionWidths[key] = Math.max(
          minColumnWidth,
          naturalNonDescriptionWidths[key] * nonDescriptionShrinkFactor
        );
      });
      const scaledNonDescriptionWidth = Object.values(finalNonDescriptionWidths).reduce((sum, width) => sum + width, 0);
      const descriptionWidth = Math.max(1, tableWidth - scaledNonDescriptionWidth);
      const columnLines = [tableLeftX];
      const colPositions = {};
      let currentX = tableLeftX;
      activeColumns.forEach((column) => {
        const width = column.key === 'description'
          ? descriptionWidth
          : finalNonDescriptionWidths[column.key];
        colPositions[column.key] = { left: currentX, right: currentX + width };
        currentX += width;
        columnLines.push(currentX);
      });
      columnLines[columnLines.length - 1] = tableRightX;
      if (activeColumns.length > 0) {
        const lastKey = activeColumns[activeColumns.length - 1].key;
        colPositions[lastKey].right = tableRightX;
      }
      const colWidth = (left, right) => Math.max(1, right - left - (columnPadding * 2));
      const hasColumn = (key) => !!colPositions[key];
      const clampTableCloseY = (value) => Math.min(value, getCurrentContentBottomY() - 1);
      let pageTableTopY = currentY;
      const closeProductsTablePage = (endY) => {
        const safeEndY = Math.max(pageTableTopY + 1, Math.min(endY, getCurrentContentBottomY() - 1));
        doc.setLineWidth(0.2);
        doc.line(tableLeftX, safeEndY, tableRightX, safeEndY);
        columnLines.forEach((x) => {
          doc.line(x, pageTableTopY, x, safeEndY);
        });
      };
      const drawProductsHeader = (startY) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        const headerTopY = startY;
        pageTableTopY = headerTopY;
        const headerTextY = headerTopY + 3;
        const colWidth = (left, right) => Math.max(1, right - left - (columnPadding * 2));
        const headerLineHeight = 3;
        const headerLinesMap = new Map(
          activeColumns.map((column) => {
            const position = colPositions[column.key];
            return [column.key, wrapPdfText(column.label, colWidth(position.left, position.right), column.label)];
          })
        );
        const maxHeaderLines = Math.max(1, ...Array.from(headerLinesMap.values()).map((lines) => lines.length));
        const headerRowBottomY = headerTextY + (maxHeaderLines * headerLineHeight) + 1;
        doc.setLineWidth(0.2);
        doc.line(tableLeftX, headerTopY, tableRightX, headerTopY);
        activeColumns.forEach((column) => {
          const position = colPositions[column.key];
          const headerLines = headerLinesMap.get(column.key) || [column.label];
          headerLines.forEach((line, index) => {
            doc.text(line, position.left + columnPadding, headerTextY + (index * headerLineHeight), {
              maxWidth: colWidth(position.left, position.right)
            });
          });
        });

        doc.line(tableLeftX, headerRowBottomY, tableRightX, headerRowBottomY);
        columnLines.forEach((x) => {
          doc.line(x, headerTopY, x, headerRowBottomY);
        });

        return headerRowBottomY + 3;
      };

      const drawProductsSectionHeader = (includeTitle = true) => {
        if (includeTitle) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Products', 17, currentY);
          currentY += 4; // Further reduced spacing
        }
        currentY = drawProductsHeader(currentY);
        doc.setFont('helvetica', 'normal');
      };

      drawProductsSectionHeader(false);
      
      const rowGap = 6;
      invoice.products.forEach((product, index) => {
        const rate = Number(product.price || product.rate) || 0;
        const tax = Number(product.tax) || 0;
        const quantity = Number(product.quantity) || 1;
        const lineAmount = rate * quantity;
        const taxAmount = lineAmount * (tax / 100);
        const total = lineAmount + taxAmount;
        
        // Description - multi-line support with normalization and hard wrapping
        const description = normalizePdfText(product.description, 'N/A');
        const descriptionColumnWidth = colWidth(colPositions.description.left, colPositions.description.right);
        const descriptionLines = wrapPdfText(description, descriptionColumnWidth, 'N/A');
        const lineHeight = 4;
        const cellTopPadding = 3;
        const minRowHeight = 8;

        const drawCellLines = (lines, x, startY, width) => {
          lines.forEach((line, i) => {
            doc.text(line || ' ', x, startY + (i * lineHeight), { maxWidth: width });
          });
        };

        let lineIndex = 0;
        let isFirstChunk = true;

        while (lineIndex < descriptionLines.length) {
          const currentPageBottomY = getCurrentContentBottomY();
          if (currentY + minRowHeight > currentPageBottomY) {
            closeProductsTablePage(currentY);
            addNewPage();
            drawProductsSectionHeader(false);
          }

          const availableHeight = getCurrentContentBottomY() - currentY;
          const availableLines = Math.max(1, Math.floor(availableHeight / lineHeight));
          const chunkLines = descriptionLines.slice(lineIndex, lineIndex + availableLines);

          const hsnValue = product.hsncode ?? product.hsnCode ?? product.hsn ?? product.productId?.hsncode ?? product.productId?.hsnCode ?? '-';
          const hsnLines = hasColumn('hsn')
            ? wrapPdfText(hsnValue.toString(), colWidth(colPositions.hsn.left, colPositions.hsn.right), '-')
            : [];
          const rateLines = hasColumn('rate')
            ? wrapPdfText(formatCurrency(rate), colWidth(colPositions.rate.left, colPositions.rate.right), '-')
            : [];
          const qtyLines = hasColumn('qty')
            ? wrapPdfText(quantity.toString(), colWidth(colPositions.qty.left, colPositions.qty.right), '-')
            : [];
          const amountExclTaxLines = hasColumn('amountExclTax')
            ? wrapPdfText(formatCurrency(lineAmount), colWidth(colPositions.amountExclTax.left, colPositions.amountExclTax.right), '-')
            : [];
          const taxLines = hasColumn('tax')
            ? wrapPdfText(`${tax}%`, colWidth(colPositions.tax.left, colPositions.tax.right), '-')
            : [];
          const taxTypeLines = hasColumn('taxType')
            ? wrapPdfText(product.taxType || '-', colWidth(colPositions.taxType.left, colPositions.taxType.right), '-')
            : [];
          const taxAmtLines = hasColumn('taxAmt')
            ? wrapPdfText(formatCurrency(taxAmount), colWidth(colPositions.taxAmt.left, colPositions.taxAmt.right), '-')
            : [];
          const totalLines = hasColumn('total')
            ? wrapPdfText(formatCurrency(total), colWidth(colPositions.total.left, colPositions.total.right), '-')
            : [];

          const maxLinesInRow = Math.max(
            chunkLines.length,
            hsnLines.length,
            rateLines.length,
            qtyLines.length,
            amountExclTaxLines.length,
            taxLines.length,
            taxTypeLines.length,
            taxAmtLines.length,
            totalLines.length
          );

          const rowTopY = currentY;
          const rowHeight = Math.max(minRowHeight, (maxLinesInRow * lineHeight) + cellTopPadding);
          const textStartY = rowTopY + cellTopPadding;

          if (isFirstChunk && hasColumn('sl')) {
            doc.text((index + 1).toString(), colPositions.sl.left + columnPadding, textStartY, {
              maxWidth: colWidth(colPositions.sl.left, colPositions.sl.right)
            });
          }

          drawCellLines(chunkLines, colPositions.description.left + columnPadding, textStartY, descriptionColumnWidth);

          if (isFirstChunk) {
            if (hasColumn('hsn')) {
              drawCellLines(hsnLines, colPositions.hsn.left + columnPadding, textStartY, colWidth(colPositions.hsn.left, colPositions.hsn.right));
            }
            if (hasColumn('rate')) {
              drawCellLines(rateLines, colPositions.rate.left + columnPadding, textStartY, colWidth(colPositions.rate.left, colPositions.rate.right));
            }
            if (hasColumn('qty')) {
              drawCellLines(qtyLines, colPositions.qty.left + columnPadding, textStartY, colWidth(colPositions.qty.left, colPositions.qty.right));
            }
            if (hasColumn('amountExclTax')) {
              drawCellLines(amountExclTaxLines, colPositions.amountExclTax.left + columnPadding, textStartY, colWidth(colPositions.amountExclTax.left, colPositions.amountExclTax.right));
            }
            if (hasColumn('tax')) {
              drawCellLines(taxLines, colPositions.tax.left + columnPadding, textStartY, colWidth(colPositions.tax.left, colPositions.tax.right));
            }
            if (hasColumn('taxType')) {
              drawCellLines(taxTypeLines, colPositions.taxType.left + columnPadding, textStartY, colWidth(colPositions.taxType.left, colPositions.taxType.right));
            }
            if (hasColumn('taxAmt')) {
              drawCellLines(taxAmtLines, colPositions.taxAmt.left + columnPadding, textStartY, colWidth(colPositions.taxAmt.left, colPositions.taxAmt.right));
            }
            if (hasColumn('total')) {
              drawCellLines(totalLines, colPositions.total.left + columnPadding, textStartY, colWidth(colPositions.total.left, colPositions.total.right));
            }
          }

          currentY = rowTopY + rowHeight;
          lineIndex += availableLines;
          isFirstChunk = false;

          if (lineIndex < descriptionLines.length && currentY + minRowHeight > getCurrentContentBottomY()) {
            closeProductsTablePage(currentY);
            addNewPage();
            drawProductsSectionHeader(false);
          }
        }

        if (index < invoice.products.length - 1) {
          const contentBottomY = getCurrentContentBottomY();
          const nextRowY = currentY + rowGap;
          if (nextRowY + minRowHeight > contentBottomY) {
            closeProductsTablePage(clampTableCloseY(currentY + rowGap));
            addNewPage();
            drawProductsSectionHeader(false);
          } else {
            const separatorY = Math.min(currentY + (rowGap / 2), contentBottomY - 1);
            doc.setLineDash([]);
            doc.line(tableLeftX, separatorY, tableRightX, separatorY);
            currentY = nextRowY;
          }
        }
      });

      closeProductsTablePage(clampTableCloseY(currentY + 2));
      currentY += 6; // Reduced spacing
    }

    // Calculate totals
    const pdfKey = (type || 'Invoice').toLowerCase();
    const activePdfOptions = pdfOptions || orgDetails?.metadata?.pdfOptions?.[pdfKey] || {};
    const inclusiveTax = !!activePdfOptions.inclusiveTax;
    const legacyTaxBreakup = activePdfOptions.taxBreakup;
    const hideTaxBreakup = typeof activePdfOptions.hideTaxBreakup === 'boolean'
      ? activePdfOptions.hideTaxBreakup
      : (typeof legacyTaxBreakup === 'boolean' ? legacyTaxBreakup : inclusiveTax);
    const showSubtotal = !hideTaxBreakup;
    const showTaxBreakup = !hideTaxBreakup;
    const subtotal = calculateSubtotal();
    const totalTax = calculateTotalTax();
    const totalBeforeRoundOff = subtotal + totalTax;
    const grandTotal = invoice.total || totalBeforeRoundOff;
    const roundOffTotalsEnabled = Boolean(orgDetails?.metadata?.roundOffTotals);
    const roundingOffAmount = grandTotal - totalBeforeRoundOff;
    const taxBreakdown = calculateTaxBreakdown();
    const amountInWords = `(${formatAmountInWords(grandTotal)})`;
    const amountInWordsLines = wrapPdfText(amountInWords, 120, '');
    const totalsLineCount = roundOffTotalsEnabled
      ? [true, taxBreakdown.IGST > 0, taxBreakdown['CGST/SGST'] > 0, true, true].filter(Boolean).length
      : [showSubtotal, taxBreakdown.IGST > 0 && showTaxBreakup, taxBreakdown['CGST/SGST'] > 0 && showTaxBreakup, true]
          .filter(Boolean)
          .length;

    // If totals + footer won't fit, move to a new page
    const totalsBlockHeight = (totalsLineCount * 4) + 12 + (amountInWordsLines.length * 4);
    if (currentY + totalsBlockHeight > getPaymentTableStartY() - 6) {
      addNewPage();
    }

    // Totals section (moved above payment terms)
    currentY += 2; // Minimal spacing
    const rightAlign = 193; // Align with outer border
    const valueGap = 22;

    const drawAmountLine = (label, value, y) => {
      doc.text(label, rightAlign - valueGap, y, { align: 'right' });
      doc.text(value, rightAlign, y, { align: 'right' });
    };
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let lastItemY = null;
    if (roundOffTotalsEnabled) {
      drawAmountLine('Total (excl. Tax):', formatCurrency(subtotal), currentY);
      lastItemY = currentY;
      currentY += 4;

      if (taxBreakdown.IGST > 0) {
        drawAmountLine('IGST:', formatCurrency(taxBreakdown.IGST), currentY);
        lastItemY = currentY;
        currentY += 4;
      }
      if (taxBreakdown['CGST/SGST'] > 0) {
        drawAmountLine('CGST/SGST:', formatCurrency(taxBreakdown['CGST/SGST']), currentY);
        lastItemY = currentY;
        currentY += 4;
      }

      drawAmountLine('Rounding off:', formatCurrency(roundingOffAmount), currentY);
      lastItemY = currentY;
      currentY += 4;
    } else {
      if (showSubtotal) {
        drawAmountLine('Subtotal:', formatCurrency(subtotal), currentY);
        lastItemY = currentY;
        currentY += 4; // Reduced spacing
      }

      if (showTaxBreakup) {
        if (taxBreakdown.IGST > 0) {
          drawAmountLine('IGST:', formatCurrency(taxBreakdown.IGST), currentY);
          lastItemY = currentY;
          currentY += 4; // Reduced spacing
        }
        if (taxBreakdown['CGST/SGST'] > 0) {
          drawAmountLine('CGST/SGST:', formatCurrency(taxBreakdown['CGST/SGST']), currentY);
          lastItemY = currentY;
          currentY += 4; // Reduced spacing
        }
      }
    }

    // Grand total with bold font and optional separator line
    currentY = lastItemY !== null ? lastItemY + 3 : currentY;
    if (!hideTaxBreakup || roundOffTotalsEnabled) {
      doc.setLineWidth(0.2);
      doc.line(rightAlign - 45, currentY, rightAlign, currentY);
      currentY += 4; // Reduced spacing
    }
    doc.setFont('helvetica', 'bold');
    drawAmountLine(
      'Grand Total:',
      formatCurrency(grandTotal),
      currentY
    );
    currentY += 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(amountInWordsLines, rightAlign, currentY, { align: 'right' });

    // Move to end of page for Payment Terms and Authorization Table
    const leftPaymentColumnX = 15;
    const rightPaymentColumnX = 105;
    const paymentTableWidth = 180;
    const paymentColumnWidth = 90;
    const paymentTableStartY = getPaymentTableStartY();

    // Draw payment table borders at bottom of page
    doc.setLineWidth(0.2);
    doc.rect(leftPaymentColumnX, paymentTableStartY, paymentTableWidth, paymentTableHeight);
    doc.line(rightPaymentColumnX, paymentTableStartY, rightPaymentColumnX, paymentTableStartY + paymentTableHeight);

    // Column 1 - Payment Terms
    let paymentY = paymentTableStartY + 6;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(`Cheque or DD in favour of ${companyName || 'Company Name'}`, leftPaymentColumnX + 3, paymentY);
    paymentY += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.text('NEFT', leftPaymentColumnX + 3, paymentY);
    paymentY += 4;
    const primaryBankAccount = Array.isArray(orgDetails?.bankAccounts) && orgDetails.bankAccounts.length > 0
      ? orgDetails.bankAccounts.find((account) => account.type === 'Primary') || orgDetails.bankAccounts[0]
      : null;
    const bankAccount = selectedBankAccount || primaryBankAccount;
    const bankName = bankAccount?.bankName || orgDetails?.bankName || 'XXXX';
    const accountNumber = bankAccount?.accountNumber || orgDetails?.accountNumber || 'XXXX';
    const ifscCode = bankAccount?.ifscCode || orgDetails?.ifscCode || 'XXXX';

    doc.text(`Bank: ${bankName}`, leftPaymentColumnX + 3, paymentY);
    paymentY += 4;
    doc.text(`A/C No: ${accountNumber}`, leftPaymentColumnX + 3, paymentY);
    paymentY += 4;
    doc.text(`IFSC: ${ifscCode}`, leftPaymentColumnX + 3, paymentY);
    paymentY += 8;
    
    doc.setFont('helvetica', 'bold');
    doc.text('NOTE:', leftPaymentColumnX + 3, paymentY);
    paymentY += 4;
    
    doc.setFont('helvetica', 'normal');
    doc.text('i) Goods once sold cannot be taken back', leftPaymentColumnX + 8, paymentY);
    paymentY += 4;
    doc.text('ii) All payments to be received within 15 days of invoice', leftPaymentColumnX + 8, paymentY);
    paymentY += 4;
    doc.text('iii) Subject to Chennai Jurisdiction', leftPaymentColumnX + 8, paymentY);

    // Column 2 - Authorization
    let authY = paymentTableStartY + 20; // Start from middle of table for vertical spacing
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`For ${companyName || 'Company Name'},`, rightPaymentColumnX + 3, authY);
    
    authY += 25; // Evenly spaced from first text
    doc.setFont('helvetica', 'bold');
    doc.text('AUTHORISED SIGNATORY', rightPaymentColumnX + 3, authY);

    if (activePdfOptions.addTermsAndConditions) {
      renderTermsAndConditionsAppendix(activePdfOptions.termsAndConditionsText || '');
    }

    // Safety pass: enforce additional-page chrome on every page after page 1.
    const activePageBeforeChromePass = doc.getCurrentPageInfo().pageNumber;
    const totalPages = doc.getNumberOfPages();
    for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
      if (!pagesWithAdditionalChrome.has(pageNumber)) {
        doc.setPage(pageNumber);
        drawAdditionalPageChrome();
      }
    }
    doc.setPage(activePageBeforeChromePass);

    if (activePdfOptions.showPageNumbers) {
      const activePageBeforeNumberPass = doc.getCurrentPageInfo().pageNumber;
      const pageCount = doc.getNumberOfPages();
      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        doc.setPage(pageNumber);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`${pageNumber}/${pageCount}`, 105, 293, { align: 'center' });
      }
      doc.setPage(activePageBeforeNumberPass);
    }

    // Generate filename
    const fileName = `${type || 'Invoice'}-${invoice.documentNumber || invoice._id || 'Unknown'}.pdf`;
    
    // Save the PDF
    doc.save(fileName);
    
    console.log(`PDF generated successfully: ${fileName}`);
    return true;
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Failed to generate PDF. Please try again.');
    return false;
  }
};